import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X, Hand, Clock, Eye, EyeOff, ArrowRight, AlertTriangle, Brain } from 'lucide-react';
import {
  TRAIL_NODES, TRAIL_SEQUENCE,
  NAMING_ANSWERS,
  DIGIT_FORWARD, DIGIT_BACKWARD,
  VIGILANCE_LETTERS,
  MEMORY_WORDS,
  matchesAnswer, matchRecalledWords, countFluency,
} from '../../lib/assessments/moca';

// =============================================================================
// MoCA — Interactive step components.
//
// Patient self-administered variants of each MoCA item that can reasonably be
// done by the patient on a device without a provider observing. Each component
// receives an `answer` prop (the persisted state for this step) and an
// `onChange` callback to write a partial answer back.
//
// Steps that CAN'T be self-administered (cube drawing, clock drawing, sentence
// repetition, abstraction similarities) fall back to <InteractiveProviderHandoff>
// which shows a patient-facing prompt and a "your provider will mark this"
// message — the provider scores it from the same screen when they reach the
// patient at the end of the visit.
// =============================================================================

// ---------------- Shared atoms ----------------------------------------------

function PatientPrompt({ heading, body, kicker }) {
  return (
    <div className="mci-prompt">
      {kicker && <div className="mci-prompt-kicker">{kicker}</div>}
      <div className="mci-prompt-heading">{heading}</div>
      {body && <p className="mci-prompt-body">{body}</p>}
    </div>
  );
}

function LiveScoreBadge({ score, max, complete }) {
  return (
    <div className={`mci-live ${complete ? 'is-complete' : ''}`}>
      <span className="mci-live-num">{score}</span>
      <span className="mci-live-of">/ {max}</span>
      {complete && <Check size={11} strokeWidth={3} />}
    </div>
  );
}

// ---------------- Provider hand-off (for non-self-administrable items) -------

export function InteractiveProviderHandoff({ step, answer, onChange, patientHeading, patientBody }) {
  const score = answer?.score;
  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="On paper or out loud"
        heading={patientHeading}
        body={patientBody}
      />
      <div className="mci-handoff">
        <div className="mci-handoff-icon"><Hand size={18} strokeWidth={2.2} /></div>
        <div>
          <div className="mci-handoff-title">When you're done, your provider will check it</div>
          <div className="mci-handoff-sub">They'll mark a score on this device — it'll only take a second.</div>
        </div>
      </div>

      {/* Provider scoring — quietly available below */}
      <details className="mci-disclose">
        <summary>Provider scoring</summary>
        <div className="mci-disclose-body">
          <div className="mci-section-label" style={{ marginBottom: 10 }}>
            Score this item ({step.max} {step.max === 1 ? 'pt' : 'pts'} max)
          </div>
          {step.max === 1 ? (
            <div className="moca-binary">
              <button
                type="button"
                onClick={() => onChange({ score: 0 })}
                className={`moca-choice moca-choice-no ${score === 0 ? 'is-selected' : ''}`}
              >
                <X size={20} strokeWidth={2.2} />
                <span className="moca-choice-label">Incorrect</span>
                <span className="moca-choice-points">0 pts</span>
              </button>
              <button
                type="button"
                onClick={() => onChange({ score: 1 })}
                className={`moca-choice moca-choice-yes ${score === 1 ? 'is-selected' : ''}`}
              >
                <Check size={20} strokeWidth={2.2} />
                <span className="moca-choice-label">Correct</span>
                <span className="moca-choice-points">+1 pt</span>
              </button>
            </div>
          ) : (
            <div className="mci-stepper">
              {Array.from({ length: step.max + 1 }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onChange({ score: i })}
                  className={`mci-stepper-btn ${score === i ? 'is-selected' : ''}`}
                >
                  {i}
                </button>
              ))}
            </div>
          )}
          <div className="mci-disclose-rule">{step.scoring_rule}</div>
        </div>
      </details>
    </div>
  );
}

// ---------------- Intro -----------------------------------------------------

export function InteractiveIntro() {
  return (
    <div className="mci-step">
      <div className="mci-intro">
        <div className="mci-intro-icon"><Brain size={28} strokeWidth={2.2} /></div>
        <h2 className="mci-intro-title">A quick check-in for your brain.</h2>
        <p className="mci-intro-body">
          You'll work through a few short activities — most are tap-or-type
          right here on your device. A couple of them you'll do on paper and
          show your provider. The whole thing usually takes about ten minutes.
        </p>
        <ul className="mci-intro-notes">
          <li><span className="mci-intro-dot" />Take your time. There's no rush.</li>
          <li><span className="mci-intro-dot" />If you don't know an answer, that's fine — just continue.</li>
          <li><span className="mci-intro-dot" />Your provider will go over the results with you afterwards.</li>
        </ul>
      </div>
    </div>
  );
}

// ---------------- Trail Making ----------------------------------------------

export function InteractiveTrail({ answer, onChange }) {
  // `taps` is the order of nodes the patient has tapped so far.
  const taps = answer?.taps || [];
  const errors = answer?.errors || 0;
  const targetIndex = taps.length; // next expected node
  const isDone = taps.length === TRAIL_SEQUENCE.length && errors === 0;

  const handleTap = (id) => {
    if (taps.includes(id)) return; // already used
    const expected = TRAIL_SEQUENCE[targetIndex];
    if (id === expected) {
      const nextTaps = [...taps, id];
      const done = nextTaps.length === TRAIL_SEQUENCE.length;
      onChange({
        taps: nextTaps,
        errors,
        // Score: 1 if completed in correct order with no errors.
        score: done && errors === 0 ? 1 : 0,
      });
    } else {
      onChange({ taps, errors: errors + 1, score: 0 });
    }
  };

  const reset = () => onChange({ taps: [], errors: 0, score: null });

  // Pre-compute path between consecutive correct taps for the trail line.
  const pathPoints = useMemo(() => {
    return taps.map(id => TRAIL_NODES.find(n => n.id === id));
  }, [taps]);

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Connect the dots"
        heading="Tap the circles in this order: 1 → A → 2 → B → 3 → C → 4 → D → 5 → E"
        body="Start at 1, then go to A, then 2, then B, and keep going — switching between numbers and letters."
      />

      <div className="mci-trail-frame">
        <svg viewBox="0 0 100 100" className="mci-trail-svg" preserveAspectRatio="xMidYMid meet">
          {/* Connecting line as you go */}
          {pathPoints.length > 1 && (
            <polyline
              points={pathPoints.map(n => `${n.x},${n.y}`).join(' ')}
              fill="none"
              stroke="var(--teal)"
              strokeWidth="0.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {/* Nodes */}
          {TRAIL_NODES.map(n => {
            const tapped = taps.includes(n.id);
            const expected = !tapped && TRAIL_SEQUENCE[targetIndex] === n.id;
            return (
              <g
                key={n.id}
                style={{ cursor: tapped ? 'default' : 'pointer' }}
                onClick={() => handleTap(n.id)}
              >
                <circle
                  cx={n.x}
                  cy={n.y}
                  r="5.5"
                  fill={tapped ? 'var(--teal)' : '#ffffff'}
                  stroke={expected ? 'var(--teal)' : '#0b0b14'}
                  strokeWidth={expected ? '0.9' : '0.4'}
                  className={expected ? 'mci-trail-pulse' : ''}
                />
                <text
                  x={n.x}
                  y={n.y + 1.6}
                  textAnchor="middle"
                  fontSize="4.5"
                  fontWeight="700"
                  fill={tapped ? '#fff' : '#0b0b14'}
                  pointerEvents="none"
                >
                  {n.label}
                </text>
                {n.sublabel && (
                  <text
                    x={n.x}
                    y={n.y + 9}
                    textAnchor="middle"
                    fontSize="2.5"
                    fill="rgba(11,11,20,0.55)"
                    pointerEvents="none"
                  >
                    {n.sublabel}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mci-trail-status">
        <div>
          {isDone
            ? <span className="text-success">Perfect — no errors.</span>
            : taps.length === TRAIL_SEQUENCE.length
              ? <span className="text-warning">Completed with {errors} error{errors === 1 ? '' : 's'}.</span>
              : <>Next: <strong>{TRAIL_SEQUENCE[targetIndex] || '—'}</strong></>}
          {errors > 0 && taps.length < TRAIL_SEQUENCE.length && (
            <span className="mci-trail-err"> · {errors} wrong tap{errors === 1 ? '' : 's'}</span>
          )}
        </div>
        <button onClick={reset} type="button" className="mci-link-btn">Start over</button>
      </div>

      <LiveScoreBadge score={answer?.score ?? 0} max={1} complete={isDone} />
    </div>
  );
}

// ---------------- Naming (animal images + typed answers) --------------------

function AnimalSvg({ kind }) {
  // Minimal but identifiable silhouettes. Inline so they ship with the bundle
  // — no extra HTTP requests, theme-able via currentColor.
  const stroke = 'currentColor';
  if (kind === 'lion') {
    return (
      <svg viewBox="0 0 100 100" className="mci-animal" aria-label="Lion">
        {/* Mane — radial spikes */}
        <g fill={stroke}>
          {Array.from({ length: 14 }).map((_, i) => {
            const a = (i / 14) * Math.PI * 2;
            const cx = 50 + Math.cos(a) * 28;
            const cy = 52 + Math.sin(a) * 28;
            return <circle key={i} cx={cx} cy={cy} r="6" opacity="0.92" />;
          })}
        </g>
        {/* Face */}
        <circle cx="50" cy="52" r="22" fill="#fff" stroke={stroke} strokeWidth="1.6" />
        {/* Ears */}
        <circle cx="36" cy="34" r="5" fill={stroke} />
        <circle cx="64" cy="34" r="5" fill={stroke} />
        {/* Eyes */}
        <circle cx="43" cy="50" r="2.2" fill={stroke} />
        <circle cx="57" cy="50" r="2.2" fill={stroke} />
        {/* Nose */}
        <path d="M50 56 L46 60 L54 60 Z" fill={stroke} />
        {/* Mouth */}
        <path d="M50 60 Q50 66 45 66 M50 60 Q50 66 55 66" fill="none" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'rhino') {
    return (
      <svg viewBox="0 0 100 100" className="mci-animal" aria-label="Rhinoceros">
        {/* Body silhouette — side profile */}
        <path
          d="M14 62
             C 14 52, 26 46, 36 46
             L 56 46
             C 64 46, 70 50, 72 56
             L 76 56
             C 80 56, 82 58, 82 62
             L 82 72
             C 82 76, 79 78, 76 78
             L 76 84 L 70 84 L 70 78
             L 30 78 L 30 84 L 24 84 L 24 78
             C 18 78, 14 74, 14 68 Z"
          fill={stroke}
        />
        {/* Horn */}
        <path d="M64 46 L66 30 L72 46 Z" fill={stroke} />
        {/* Small back horn */}
        <path d="M52 46 L54 38 L58 46 Z" fill={stroke} />
        {/* Eye */}
        <circle cx="68" cy="56" r="1.6" fill="#fff" />
        {/* Ear */}
        <path d="M54 44 L52 36 L58 40 Z" fill={stroke} />
      </svg>
    );
  }
  if (kind === 'camel') {
    return (
      <svg viewBox="0 0 100 100" className="mci-animal" aria-label="Camel">
        {/* Body + humps + neck + head */}
        <path
          d="M14 76
             L 14 64
             C 14 60, 18 56, 22 58
             C 26 50, 32 46, 38 50
             C 42 44, 50 44, 54 50
             C 60 46, 68 50, 70 58
             C 70 50, 72 40, 78 36
             C 82 33, 85 34, 84 38
             L 82 50
             C 88 52, 90 56, 88 60
             C 90 64, 86 66, 84 64
             L 84 76 L 78 76 L 78 72
             L 70 72 L 70 76 L 64 76 L 64 72
             L 30 72 L 30 76 L 24 76 L 24 72
             L 20 72 L 20 76 Z"
          fill={stroke}
        />
        {/* Eye */}
        <circle cx="80" cy="44" r="1.4" fill="#fff" />
        {/* Mouth */}
        <path d="M85 46 L88 46" stroke="#fff" strokeWidth="0.8" />
      </svg>
    );
  }
  return null;
}

export function InteractiveNaming({ answer, onChange }) {
  const inputs = answer?.inputs || {};
  const items = [
    { id: 'lion',  kind: 'lion',  accepted: NAMING_ANSWERS.lion },
    { id: 'rhino', kind: 'rhino', accepted: NAMING_ANSWERS.rhino },
    { id: 'camel', kind: 'camel', accepted: NAMING_ANSWERS.camel },
  ];

  const handleChange = (id, value) => {
    const next = { ...inputs, [id]: value };
    let score = 0;
    const correct = {};
    for (const it of items) {
      const ok = matchesAnswer(next[it.id] || '', it.accepted);
      correct[it.id] = ok;
      if (ok) score += 1;
    }
    onChange({ inputs: next, correct, score });
  };

  const score = items.reduce((s, it) => s + (matchesAnswer(inputs[it.id] || '', it.accepted) ? 1 : 0), 0);

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Name the animal"
        heading="What is each of these animals called?"
        body="Type the name in the box below each picture. Spelling doesn't have to be perfect."
      />

      <div className="mci-naming-grid">
        {items.map(it => {
          const value = inputs[it.id] || '';
          const ok = value && matchesAnswer(value, it.accepted);
          return (
            <div key={it.id} className="mci-naming-card">
              <div className="mci-naming-frame">
                <AnimalSvg kind={it.kind} />
              </div>
              <div className="mci-naming-input-wrap">
                <input
                  value={value}
                  onChange={e => handleChange(it.id, e.target.value)}
                  placeholder="Type the name…"
                  className={`mci-naming-input ${ok ? 'is-ok' : value ? 'is-typing' : ''}`}
                />
                {ok && <Check size={14} strokeWidth={3} className="mci-naming-tick" />}
              </div>
            </div>
          );
        })}
      </div>

      <LiveScoreBadge score={score} max={3} complete={score === 3} />
    </div>
  );
}

// ---------------- Memory Registration (just show the words) -----------------

export function InteractiveMemory({ answer, onChange }) {
  const [trial, setTrial] = useState(answer?.trial || 1);
  const [phase, setPhase] = useState('idle'); // idle | showing | answering

  useEffect(() => {
    if (phase !== 'showing') return;
    const t = setTimeout(() => setPhase('answering'), 5000);
    return () => clearTimeout(t);
  }, [phase]);

  const startTrial = () => setPhase('showing');

  const recordTrial = (typed) => {
    // No scoring here — but capture what was typed for clinical reference.
    const matched = matchRecalledWords(typed);
    const key = `trial${trial}`;
    onChange({
      ...(answer || {}),
      [key]: { typed, matched: Array.from(matched) },
      trial,
      score: 0,
    });
    if (trial === 1) {
      setTrial(2);
      setPhase('idle');
    } else {
      setPhase('done');
    }
  };

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker={`Memory · trial ${trial} of 2`}
        heading="We'll show you five words. Try to remember them all."
        body="They'll appear for a few seconds, then disappear. Tell us as many as you can remember. Don't worry if you miss a few — we'll ask you again later in the test."
      />

      {phase === 'idle' && (
        <button onClick={startTrial} type="button" className="mci-cta">
          {trial === 1 ? "I'm ready — show me the words" : 'Show me the words again'}
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      )}

      {phase === 'showing' && (
        <div className="mci-memory-words">
          {MEMORY_WORDS.map(w => <span key={w}>{w}</span>)}
        </div>
      )}

      {phase === 'answering' && <MemoryAnswerForm onSubmit={recordTrial} trial={trial} />}

      {phase === 'done' && (
        <div className="mci-memory-done">
          <Check size={18} strokeWidth={2.5} className="text-success" />
          <div>
            <div className="mci-memory-done-title">Got it — moving on.</div>
            <div className="mci-memory-done-sub">We'll ask you to recall these words again later.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemoryAnswerForm({ onSubmit, trial }) {
  const [text, setText] = useState('');
  return (
    <div className="mci-recall-input">
      <label className="mci-recall-label">
        Type the words you remember <span className="text-muted">(separate them with spaces or commas)</span>
      </label>
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="e.g., face, velvet…"
        rows={2}
        autoFocus
        className="moca-input moca-textarea"
      />
      <button onClick={() => onSubmit(text)} type="button" className="mci-cta" disabled={!text.trim()}>
        {trial === 1 ? 'Submit — show me the list again' : "I'm done"}
        <ArrowRight size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ---------------- Digit Span (forward / backward) ---------------------------

function DigitSpan({ digits, expected, label, answer, onChange }) {
  const [phase, setPhase] = useState(answer?.score != null ? 'done' : 'idle');
  const [shownIndex, setShownIndex] = useState(-1);
  const [typed, setTyped] = useState(answer?.typed || '');

  useEffect(() => {
    if (phase !== 'showing') return;
    if (shownIndex >= digits.length - 1) {
      const t = setTimeout(() => setPhase('answering'), 900);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setShownIndex(i => i + 1), 900);
    return () => clearTimeout(t);
  }, [phase, shownIndex, digits.length]);

  const start = () => { setShownIndex(0); setPhase('showing'); };
  const submit = () => {
    const clean = typed.replace(/\D/g, '');
    const ok = clean === expected;
    onChange({ typed: clean, score: ok ? 1 : 0 });
    setPhase('done');
  };
  const redo = () => {
    setTyped(''); setShownIndex(-1); setPhase('idle');
    onChange({ typed: '', score: null });
  };

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker={label}
        heading={
          label === 'Backward'
            ? "Listen — then say them back in reverse."
            : 'Listen — then repeat them back in the same order.'
        }
        body={
          label === 'Backward'
            ? "We'll show you a short sequence of numbers. When it disappears, type them in the OPPOSITE order. If we show 1-2-3, you'd type 3-2-1."
            : "We'll show you a short sequence of numbers, one at a time. When it disappears, type them back in the same order."
        }
      />

      {phase === 'idle' && (
        <button onClick={start} type="button" className="mci-cta">
          Show me the numbers <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      )}

      {phase === 'showing' && (
        <div className="mci-digit-stage">
          <div className="mci-digit-big">{digits[shownIndex]}</div>
          <div className="mci-digit-progress">
            {digits.map((_, i) => (
              <span key={i} className={`mci-digit-pip ${i <= shownIndex ? 'is-on' : ''}`} />
            ))}
          </div>
        </div>
      )}

      {phase === 'answering' && (
        <div className="mci-numeric">
          <input
            value={typed}
            onChange={e => setTyped(e.target.value.replace(/\D/g, ''))}
            placeholder={label === 'Backward' ? 'Type the numbers in REVERSE' : 'Type the numbers'}
            inputMode="numeric"
            autoFocus
            className="moca-numeric-input"
            style={{ width: 220, letterSpacing: '0.3em' }}
          />
          <button onClick={submit} disabled={!typed} className="mci-cta" type="button">
            Submit <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {phase === 'done' && (
        <div className="mci-result-card">
          {answer?.score === 1
            ? <><Check size={16} strokeWidth={3} className="text-success" /><span className="text-success">Correct — {expected}</span></>
            : <><X size={16} strokeWidth={3} className="text-warning" /><span className="text-warning">You typed {typed || '—'} · correct was {expected}</span></>}
          <button onClick={redo} type="button" className="mci-link-btn" style={{ marginLeft: 'auto' }}>Try again</button>
        </div>
      )}

      <LiveScoreBadge score={answer?.score ?? 0} max={1} complete={phase === 'done'} />
    </div>
  );
}

export function InteractiveDigitForward({ answer, onChange }) {
  return (
    <DigitSpan
      digits={DIGIT_FORWARD}
      expected={DIGIT_FORWARD.join('')}
      label="Forward"
      answer={answer}
      onChange={onChange}
    />
  );
}
export function InteractiveDigitBackward({ answer, onChange }) {
  return (
    <DigitSpan
      digits={DIGIT_BACKWARD}
      expected={[...DIGIT_BACKWARD].reverse().join('')}
      label="Backward"
      answer={answer}
      onChange={onChange}
    />
  );
}

// ---------------- Vigilance -------------------------------------------------

export function InteractiveVigilance({ answer, onChange }) {
  // Letters reveal one per second. The patient taps a big button when they
  // see an A. We score the whole thing once the stream is done.
  const [phase, setPhase] = useState(answer?.score != null ? 'done' : 'idle');
  const [index, setIndex] = useState(-1);
  const tapsRef = useRef([]); // indices the patient tapped on

  useEffect(() => {
    if (phase !== 'running') return;
    if (index >= VIGILANCE_LETTERS.length - 1) {
      const t = setTimeout(() => {
        // Compute score: count missed As and false alarms.
        const trueAs = VIGILANCE_LETTERS.reduce((set, l, i) => { if (l === 'A') set.add(i); return set; }, new Set());
        const tappedAt = new Set(tapsRef.current);
        let missed = 0; trueAs.forEach(i => { if (!tappedAt.has(i)) missed += 1; });
        let false_ = 0; tappedAt.forEach(i => { if (!trueAs.has(i)) false_ += 1; });
        const errors = missed + false_;
        const score = errors < 2 ? 1 : 0;
        onChange({ score, errors, missed, false_, taps: tapsRef.current });
        setPhase('done');
      }, 600);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setIndex(i => i + 1), 1000);
    return () => clearTimeout(t);
  }, [phase, index, onChange]);

  const start = () => { tapsRef.current = []; setIndex(0); setPhase('running'); };
  const tap = () => {
    if (phase !== 'running' || index < 0) return;
    if (!tapsRef.current.includes(index)) tapsRef.current.push(index);
  };
  const reset = () => { setIndex(-1); setPhase('idle'); onChange({ score: null }); };

  const currentLetter = phase === 'running' && index >= 0 ? VIGILANCE_LETTERS[index] : null;
  const tappedNow = phase === 'running' && index >= 0 && tapsRef.current.includes(index);

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Vigilance"
        heading="Tap when you see the letter A."
        body="Letters will appear one at a time. Tap the big button below whenever you see A — and only A. Try not to tap for the other letters."
      />

      {phase === 'idle' && (
        <button onClick={start} type="button" className="mci-cta">
          Start the letters <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      )}

      {phase === 'running' && (
        <div className="mci-vig">
          <div className="mci-vig-stage">
            <div className="mci-vig-letter" key={index}>{currentLetter}</div>
            <div className="mci-vig-progress">
              {index + 1} / {VIGILANCE_LETTERS.length}
            </div>
          </div>
          <button
            onClick={tap}
            type="button"
            className={`mci-vig-tap ${tappedNow ? 'is-tapped' : ''} ${currentLetter === 'A' ? 'is-target' : ''}`}
          >
            <Hand size={28} strokeWidth={2.2} />
            <span>TAP FOR A</span>
          </button>
        </div>
      )}

      {phase === 'done' && (
        <div className="mci-result-card">
          {answer?.score === 1
            ? <><Check size={16} strokeWidth={3} className="text-success" /><span className="text-success">{answer.errors} error{answer.errors === 1 ? '' : 's'} — within range (+1 pt)</span></>
            : <><AlertTriangle size={16} strokeWidth={2.5} className="text-warning" /><span className="text-warning">{answer.errors} errors · {answer.missed} missed, {answer.false_} false (0 pt)</span></>}
          <button onClick={reset} type="button" className="mci-link-btn" style={{ marginLeft: 'auto' }}>Restart</button>
        </div>
      )}

      <LiveScoreBadge score={answer?.score ?? 0} max={1} complete={phase === 'done'} />
    </div>
  );
}

// ---------------- Serial 7s -------------------------------------------------

export function InteractiveSerial7({ step, answer, onChange }) {
  const values = answer?.values || {};
  const handle = (n, v) => {
    const next = { ...values, [n]: v };
    let correct = 0;
    step.targets.forEach(t => {
      const ok = parseInt(next[t], 10) === t;
      if (ok) correct += 1;
    });
    let score = 0;
    if (correct >= 4) score = 3;
    else if (correct >= 2) score = 2;
    else if (correct === 1) score = 1;
    onChange({ values: next, correctCount: correct, score });
  };
  const correctCount = step.targets.filter(t => parseInt(values[t], 10) === t).length;

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Subtraction"
        heading="Start at 100 and keep subtracting 7."
        body="Type each answer in the box. So the first box is 100 minus 7. The next box is your last answer minus 7. Take your time."
      />

      <div className="mci-s7-row">
        {step.targets.map((t, i) => {
          const v = values[t] || '';
          const n = parseInt(v, 10);
          const filled = v !== '';
          const ok = filled && n === t;
          return (
            <div key={t} className="mci-s7-cell">
              <div className="mci-s7-eq">
                {i === 0 ? '100 − 7' : `${step.targets[i - 1]} − 7`}
              </div>
              <input
                type="number"
                inputMode="numeric"
                value={v}
                onChange={e => handle(t, e.target.value)}
                className={`mci-s7-input ${filled ? (ok ? 'is-ok' : 'is-no') : ''}`}
              />
              {filled && (ok
                ? <Check size={12} strokeWidth={3} className="text-success" />
                : <X size={12} strokeWidth={3} className="text-warning" />)}
            </div>
          );
        })}
      </div>

      <div className="mci-section-label">
        {correctCount} of 5 correct · scores {answer?.score ?? 0}/3
      </div>

      <LiveScoreBadge score={answer?.score ?? 0} max={3} complete={Object.keys(values).length === 5} />
    </div>
  );
}

// ---------------- F-fluency -------------------------------------------------

export function InteractiveFluency({ step, answer, onChange }) {
  const [text, setText] = useState(answer?.text || '');
  const [timeLeft, setTimeLeft] = useState(answer?.score != null ? 0 : 60);
  const [phase, setPhase] = useState(answer?.score != null ? 'done' : 'idle');

  useEffect(() => {
    if (phase !== 'running' || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, timeLeft]);

  useEffect(() => {
    if (phase === 'running' && timeLeft === 0) {
      const count = countFluency(text);
      const score = count >= step.threshold ? 1 : 0;
      onChange({ text, count, score });
      setPhase('done');
    }
  }, [phase, timeLeft, text, step.threshold, onChange]);

  const start = () => { setText(''); setTimeLeft(60); setPhase('running'); };
  const reset = () => { setText(''); setTimeLeft(60); setPhase('idle'); onChange({ score: null }); };

  const liveCount = countFluency(text);
  const pct = (timeLeft / 60) * 100;

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Word fluency"
        heading="Name as many words as you can that start with F."
        body={`You'll have one minute. Type one word at a time — any word that starts with F counts, except for names of people or places. Aim for ${step.threshold} or more.`}
      />

      {phase === 'idle' && (
        <button onClick={start} type="button" className="mci-cta">
          Start the timer <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      )}

      {phase === 'running' && (
        <div className="mci-fluency">
          <div className="mci-fluency-bar">
            <div className="mci-fluency-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="mci-fluency-meta">
            <div><Clock size={12} strokeWidth={2.5} /> {timeLeft}s</div>
            <div className={liveCount >= step.threshold ? 'text-success' : 'text-muted'}>
              {liveCount} words {liveCount >= step.threshold ? '· goal reached!' : `· need ${step.threshold - liveCount} more`}
            </div>
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="fast, falcon, friend, family…"
            autoFocus
            rows={5}
            className="moca-input moca-textarea"
          />
        </div>
      )}

      {phase === 'done' && (
        <div className="mci-result-card">
          {answer?.score === 1
            ? <><Check size={16} strokeWidth={3} className="text-success" /><span className="text-success">{answer.count} F-words · scores 1 pt</span></>
            : <><AlertTriangle size={16} strokeWidth={2.5} className="text-warning" /><span className="text-warning">{answer.count} F-words · scores 0 pts (need ≥ {step.threshold})</span></>}
          <button onClick={reset} type="button" className="mci-link-btn" style={{ marginLeft: 'auto' }}>Try again</button>
        </div>
      )}

      <LiveScoreBadge score={answer?.score ?? 0} max={1} complete={phase === 'done'} />
    </div>
  );
}

// ---------------- Delayed Recall (uncued) -----------------------------------

export function InteractiveRecall({ answer, onChange }) {
  const [text, setText] = useState(answer?.typed || '');
  const matched = matchRecalledWords(text);
  const score = matched.size;

  const handle = (v) => {
    setText(v);
    const m = matchRecalledWords(v);
    const recalled = MEMORY_WORDS.reduce((acc, w) => { acc[w] = m.has(w); return acc; }, {});
    onChange({ typed: v, recalled, score: m.size });
  };

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Memory check"
        heading="Earlier we showed you five words. Which can you remember?"
        body="Type whichever ones come to mind — in any order, separated by spaces or commas. It's OK if you can't remember them all."
      />

      <div className="mci-recall-input">
        <textarea
          value={text}
          onChange={e => handle(e.target.value)}
          placeholder="e.g., red church face…"
          rows={3}
          autoFocus
          className="moca-input moca-textarea"
          style={{ fontSize: 16, letterSpacing: '0.04em' }}
        />
      </div>

      <div className="mci-section-label">Matched so far <span className="moca-running">({score}/5)</span></div>
      <div className="mci-recall-pills">
        {MEMORY_WORDS.map(w => {
          const on = matched.has(w);
          return (
            <span key={w} className={`mci-recall-pill ${on ? 'is-on' : ''}`}>
              {on ? <Check size={11} strokeWidth={3} /> : <EyeOff size={11} />}
              {on ? w : '?????'}
            </span>
          );
        })}
      </div>

      <LiveScoreBadge score={score} max={5} complete={score === 5} />
    </div>
  );
}

// ---------------- Orientation -----------------------------------------------

function fmtToday() {
  return new Date();
}
function dayName(d) {
  return d.toLocaleDateString(undefined, { weekday: 'long' });
}

export function InteractiveOrientation({ answer, onChange }) {
  const now = fmtToday();
  const expected = {
    date:  now.getDate(),
    month: now.getMonth() + 1,
    year:  now.getFullYear(),
    day:   dayName(now).toLowerCase(),
  };
  const values = answer?.values || {};

  const fields = [
    { id: 'date',  label: "Today's date",   placeholder: 'e.g., 8',  type: 'number' },
    { id: 'month', label: 'Month',          placeholder: 'e.g., 6 or June', type: 'text' },
    { id: 'year',  label: 'Year',           placeholder: 'e.g., 2026', type: 'number' },
    { id: 'day',   label: 'Day of the week', placeholder: 'e.g., Monday', type: 'text' },
    { id: 'place', label: 'Where are you right now?', placeholder: 'Name of the clinic', type: 'text' },
    { id: 'city',  label: 'What city?',     placeholder: 'e.g., Austin', type: 'text' },
  ];

  const isCorrect = (id, v) => {
    if (!v) return false;
    const s = String(v).trim().toLowerCase();
    if (id === 'date')  return parseInt(s, 10) === expected.date;
    if (id === 'year')  return parseInt(s, 10) === expected.year;
    if (id === 'month') {
      const m = parseInt(s, 10);
      if (!Number.isNaN(m)) return m === expected.month;
      const monthName = now.toLocaleDateString(undefined, { month: 'long' }).toLowerCase();
      return s === monthName || monthName.startsWith(s.slice(0, 3));
    }
    if (id === 'day')   return s === expected.day || expected.day.startsWith(s.slice(0, 3));
    if (id === 'place') return s.length > 1;  // can't auto-validate; provider confirms
    if (id === 'city')  return s.length > 1;
    return false;
  };

  const handle = (id, v) => {
    const next = { ...values, [id]: v };
    const correct = {};
    let score = 0;
    fields.forEach(f => {
      const ok = isCorrect(f.id, next[f.id]);
      correct[f.id] = ok;
      if (ok) score += 1;
    });
    onChange({ values: next, correct, score });
  };

  const score = fields.reduce((s, f) => s + (isCorrect(f.id, values[f.id]) ? 1 : 0), 0);

  return (
    <div className="mci-step">
      <PatientPrompt
        kicker="Orientation"
        heading="A few quick questions about today."
        body="Just answer what comes to mind — short answers are fine."
      />

      <div className="mci-orient-grid">
        {fields.map(f => {
          const v = values[f.id] || '';
          const filled = v.trim().length > 0;
          const ok = filled && isCorrect(f.id, v);
          return (
            <div key={f.id} className="mci-orient-field">
              <label className="mci-orient-label">{f.label}</label>
              <div className="mci-orient-input-wrap">
                <input
                  type={f.type === 'number' ? 'number' : 'text'}
                  inputMode={f.type === 'number' ? 'numeric' : undefined}
                  value={v}
                  onChange={e => handle(f.id, e.target.value)}
                  placeholder={f.placeholder}
                  className={`mci-orient-input ${filled ? (ok ? 'is-ok' : 'is-no') : ''}`}
                />
                {ok && <Check size={13} strokeWidth={3} className="mci-orient-tick" />}
              </div>
            </div>
          );
        })}
      </div>

      <LiveScoreBadge score={score} max={6} complete={score === 6} />
    </div>
  );
}
